/**
 * Shared error-routing hook for BFF calls under TanStack Query.
 *
 * Classifies a `BffError` and:
 *   - 401  -> opens the SessionExpiredDialog (the silent-refresh path
 *             already ran in `bff-client.ts` and failed; the dialog is the
 *             last bounce before /sign-in).
 *   - 403  -> navigate to /access-denied (token valid, tnt/role mismatch).
 *   - 5xx / network -> stays as a renderable in-page ErrorState.
 *
 * The dialog state is exposed via React Context so any feature that calls
 * the BFF can call `notifySessionExpired()` and have the modal mount at
 * AppShell level. Co-locating routing here means feature code does not
 * leak auth-routing knowledge.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { BffError } from './bff-client'

export interface BffErrorState {
  readonly sessionExpiredOpen: boolean
  readonly handle: (err: unknown) => void
  readonly dismissSessionExpired: () => void
}

const BffErrorContext = createContext<BffErrorState | null>(null)

export interface BffErrorProviderProps {
  readonly children: ReactNode
}

export function BffErrorProvider({ children }: BffErrorProviderProps): JSX.Element {
  const navigate = useNavigate()
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false)

  const handle = useCallback(
    (err: unknown): void => {
      if (!(err instanceof BffError)) {
        return
      }
      if (err.kind === 'unauthorised') {
        setSessionExpiredOpen(true)
        return
      }
      if (err.kind === 'forbidden') {
        navigate('/access-denied', { replace: true, state: { reason: 'forbidden_mid_session' } })
      }
    },
    [navigate],
  )

  const value = useMemo<BffErrorState>(
    () => ({
      sessionExpiredOpen,
      handle,
      dismissSessionExpired: () => setSessionExpiredOpen(false),
    }),
    [sessionExpiredOpen, handle],
  )

  return <BffErrorContext.Provider value={value}>{children}</BffErrorContext.Provider>
}

export function useBffError(): BffErrorState {
  const ctx = useContext(BffErrorContext)
  if (!ctx) {
    // Tests that render a feature in isolation get a no-op fallback so they
    // do not need to wrap in the provider. Production code is always
    // wrapped at the AppShell layer.
    return {
      sessionExpiredOpen: false,
      handle: () => undefined,
      dismissSessionExpired: () => undefined,
    }
  }
  return ctx
}
